/**
 * Back shows the listings the reader left, not a fresh fetch of them.
 *
 * Every listing page fetches its grid and rows in the browser. Back from a
 * listing remounted the page, and every hook on it -- the browse grid, the home
 * and For You rows, the special and tag rows -- fetched again from nothing. A
 * reader who had scrolled down got an empty screen for seconds (reproduced on
 * prod, 2026-10-20: /browse/fashion held 3 tiles 1.5s after Back), and the
 * rows that came back could differ from the ones they had been looking at.
 *
 * So the answers to listing reads are kept in memory as they arrive, and on a
 * page reached by back/forward the same request is answered from that copy.
 * The page's own hooks then rebuild exactly what was on screen, at once. A
 * forward visit always goes to the network, so a fresh visit still gets fresh
 * listings and a new shuffle order on reload.
 *
 * Only GETs to the listing read endpoints, only answers that succeeded, and
 * only in this tab's memory: a reload starts empty. Bounded by size, oldest
 * out, because a phone tab that holds too much is killed by iOS
 * ([[ios-tab-oom-leaves-no-error]]).
 */
import { isPageEnteredByBack } from './navigation-direction';

/** The listing reads a page rebuilds itself from. */
const REPLAYED_PATHS = ['/api/search/', '/api/recommendations/'];

/** Total characters kept across all answers, about 8 MB of text. */
export const MAX_KEPT_CHARS = 8_000_000;

interface KeptAnswer {
  body: string;
  contentType: string;
}

const kept = new Map<string, KeptAnswer>();
let keptChars = 0;

function keep(key: string, answer: KeptAnswer): void {
  if (answer.body.length > MAX_KEPT_CHARS) return;
  const previous = kept.get(key);
  if (previous) {
    keptChars -= previous.body.length;
    kept.delete(key);
  }
  kept.set(key, answer);
  keptChars += answer.body.length;
  // Oldest first. The newest answer alone fits under the cap, so this always
  // stops before reaching it.
  for (const [oldestKey, oldest] of kept) {
    if (keptChars <= MAX_KEPT_CHARS) break;
    keptChars -= oldest.body.length;
    kept.delete(oldestKey);
  }
}

/**
 * The key for a request this module handles, or null for one it leaves alone:
 * anything but a same-origin GET to a listing read endpoint.
 */
export function replayKey(input: RequestInfo | URL, init: RequestInit | undefined, origin: string): string | null {
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  if (method !== 'GET') return null;
  const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (!REPLAYED_PATHS.some((path) => url.pathname.startsWith(path))) return null;
  return `${url.pathname}?${url.searchParams.toString()}`;
}

/**
 * Wraps `win.fetch`. Returns a function that puts the original back.
 * Installed once from pages/_app.tsx, in the browser only.
 */
export function installBackReplayFetch(win: Window & typeof globalThis): () => void {
  const original = win.fetch;
  const origin = win.location.origin;

  const wrapped: typeof fetch = async (input, init) => {
    const key = replayKey(input, init, origin);
    if (key === null) return original.call(win, input, init);

    if (isPageEnteredByBack()) {
      const answer = kept.get(key);
      if (answer) {
        return new Response(answer.body, { status: 200, headers: { 'Content-Type': answer.contentType } });
      }
    }

    const response = await original.call(win, input, init);
    if (response.ok) {
      const contentType = response.headers.get('Content-Type') ?? 'application/json';
      // Read from a copy, so the caller's body is untouched. A failed read
      // only means this answer is not kept.
      void response.clone().text().then((body) => keep(key, { body, contentType }), () => undefined);
    }
    return response;
  };

  win.fetch = wrapped;
  return () => {
    if (win.fetch === wrapped) win.fetch = original;
  };
}

/** Test seam: forget every kept answer. */
export function clearBackReplay(): void {
  kept.clear();
  keptChars = 0;
}
